import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { usePortfolioData } from "@/hooks/use-portfolio-data";
import { buildLedgerRows, formatMoney, houseName, ledgerTotals, monthByHouse, currentMonthKey, tenantName } from "@/lib/ledger";

const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function Houses() {
  const navigate = useNavigate();
  const { data, isLoading, error } = usePortfolioData(["properties", "tenants", "expenses", "rentPayments", "maintenance"]);
  const rows = useMemo(() => buildLedgerRows(data), [data]);
  const month = useMemo(() => monthByHouse(rows, data.properties, currentMonthKey()), [rows, data.properties]);

  const houses = data.properties.map((property) => {
    const id = String(property._id);
    const tenants = data.tenants.filter((t) => t.isActive && String(t.propertyId) === id);
    const year = ledgerTotals(rows.filter((r) => r.propertyId === id && new Date(r.date).getFullYear() === new Date().getFullYear()));
    return {
      id,
      name: houseName(property),
      city: [property.city, property.state].filter(Boolean).join(", "),
      tenant: tenants.map(tenantName).join(", "),
      rent: tenants.reduce((sum, t) => sum + Number(t.monthlyRent || 0), 0) || Number(property.monthlyRent || 0),
      leaseEnd: tenants.map((t) => t.leaseEndDate).filter(Boolean).sort()[0],
      monthNet: month.lines.find((line) => line.id === id)?.net ?? 0,
      yearNet: year.net,
      openJobs: data.maintenance.filter((m) => String(m.propertyId) === id && !["completed", "cancelled"].includes(m.status)).length,
    };
  });

  return (
    <PageLayout
      title="Houses"
      actions={
        <Button size="sm" asChild className="gap-1.5">
          <Link to="/properties">
            <Plus className="h-4 w-4" /> Add or edit houses
          </Link>
        </Button>
      }
    >
      {error ? <p className="mb-3 text-sm text-destructive">{error.message}</p> : null}
      <div className="hh-panel overflow-x-auto">
        <table className="hh-table min-w-[760px]">
          <thead>
            <tr>
              <th>House</th>
              <th>Tenant</th>
              <th className="num">Rent</th>
              <th className="num">This month</th>
              <th className="num">This year</th>
              <th>Lease ends</th>
              <th className="num">Open jobs</th>
            </tr>
          </thead>
          <tbody>
            {houses.map((house) => (
              <tr key={house.id} className="cursor-pointer" onClick={() => navigate(`/houses/${house.id}`)}>
                <td>
                  <Link to={`/houses/${house.id}`} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
                    {house.name}
                  </Link>
                  {house.city ? <span className="block text-xs text-muted-foreground">{house.city}</span> : null}
                </td>
                <td className="max-w-[200px] truncate">{house.tenant || <span className="text-muted-foreground">Vacant</span>}</td>
                <td className="num">{house.rent ? formatMoney(house.rent).replace(/\.00$/, "") : "—"}</td>
                <td className={`num ${house.monthNet < 0 ? "text-rose-700 dark:text-rose-300" : ""}`}>
                  {formatMoney(house.monthNet, { sign: true }).replace(/\.00$/, "")}
                </td>
                <td className={`num ${house.yearNet < 0 ? "text-rose-700 dark:text-rose-300" : ""}`}>
                  {formatMoney(house.yearNet, { sign: true }).replace(/\.00$/, "")}
                </td>
                <td className="whitespace-nowrap">{fmtDate(house.leaseEnd)}</td>
                <td className="num">{house.openJobs || <span className="text-muted-foreground">0</span>}</td>
              </tr>
            ))}
            {!houses.length ? (
              <tr>
                <td colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                  {isLoading ? "Loading…" : (
                    <>
                      No houses yet. <Link to="/properties" className="underline">Add your first house</Link>.
                    </>
                  )}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
